import React, { useState, useEffect, useRef } from "react";
import loadable from "@loadable/component";
import { filterTabStatic } from "../../static-data";
const ImageLoader = loadable(() => import("./../common/ImageLoader"));

/**
 * @returns {JSX.Element}
 * @constructor
 */

const FilterTab = () => {
  const [filterStates, setFilterStates] = useState({});
  const [visibleImageIndex, setVisibleImageIndex] = useState(0);
  const sectionRef = useRef(null);

  const updateFilterState = (index, filterType) => {
    setFilterStates((prev) => ({
      ...prev,
      [index]: filterType,
    }));
  };

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const filterItems = sectionRef.current.querySelectorAll(
          ".p-filter-tab__column:not(.p-filter-tab__column--media)",
        );
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;

        const triggerLine = viewportHeight * 0.7;

        let activeIndex = 0;

        filterItems.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          const isPastTriggerLine = rect.top <= triggerLine;
          const isOnScreen = rect.bottom >= 0 && rect.top <= viewportHeight;

          if (isPastTriggerLine && isOnScreen) {
            activeIndex = index;
          }
        });

        setVisibleImageIndex(activeIndex);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="p-filter-tab" ref={sectionRef}>
      <div className="container">
        <div className="p-filter-tab__header">
          <h2 className="p-filter-tab__main-title">
            How does Mila pack so much performance into such a small footprint?
          </h2>
          <h3 className="p-filter-tab__main-subtitle">
            Well, she's loaded with…
          </h3>
        </div>
        <div className="p-filter-tab__body">
          <div className="p-filter-tab__content">
            {filterTabStatic.map((filterData, index) => (
              <FilterTabItem
                key={index}
                data={filterData}
                index={index}
                filterType={filterStates[index] || "air"}
                onFilterChange={(filterType) =>
                  updateFilterState(index, filterType)
                }
              />
            ))}
          </div>
          <div className="p-filter-tab__media">
            <div className="p-filter-tab__media-container">
              {filterTabStatic.map((filterData, index) => (
                <FilterImages
                  key={index}
                  data={filterData}
                  filterType={filterStates[index] || "air"}
                  isVisible={index === visibleImageIndex}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FilterTab;

function FilterTabItem({ data, filterType, onFilterChange }) {
  const filterData = data[filterType];
  return (
    <div className="p-filter-tab__column">
      <div className="p-filter-tab__content">
        {data.tabButton && (
          <div className="p-filter-tab__wrap">
            <ul className="p-filter-tab__list">
              <li>
                <button
                  type="button"
                  className={`p-filter-tab__button ${
                    filterType === "air" ? "active" : ""
                  }`}
                  onClick={() => onFilterChange("air")}
                >
                  Mila Air 3
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={`p-filter-tab__button ${
                    filterType === "airMini" ? "active" : ""
                  }`}
                  onClick={() => onFilterChange("airMini")}
                >
                  Mila Air Mini
                </button>
              </li>
            </ul>
          </div>
        )}
        {filterData.title && (
          <h3 className="p-filter-tab__title">{filterData.title}</h3>
        )}
        {filterData.image && (
          <ImageLoader
            className="p-filter-tab__image p-filter-tab__image--mobile img-relative"
            placeholderAspectRatio={788 / 754}
            image={filterData.image.src}
            webp={filterData.image.webp}
            alt={filterData.title}
          />
        )}
        {filterData.highlightText && (
          <div className="p-filter-tab__highlight">
            {filterData.highlightText}
          </div>
        )}
        {filterData.description && (
          <div className="p-filter-tab__description">
            {filterData.description}
          </div>
        )}
        {filterData.highlightTextMini && (
          <p className="p-filter-tab__highlight-mini">
            {filterData.highlightTextMini}
          </p>
        )}
      </div>
    </div>
  );
}

function FilterImages({ data, filterType, isVisible }) {
  const filterData = data[filterType];
  const mediaRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return;

    const updateDynamicTop = () => {
      if (!mediaRef.current) return;

      const rect = mediaRef.current.getBoundingClientRect();
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;

      // Center the sticky image within the viewport
      const offset = Math.max((windowHeight - rect.height) / 2, 0);
      const container = mediaRef.current.parentElement;
      if (container) {
        container.style.setProperty("--dynamic-top", `${offset}px`);
      }
    };

    updateDynamicTop();
    window.addEventListener("resize", updateDynamicTop);

    return () => {
      window.removeEventListener("resize", updateDynamicTop);
    };
  }, [isVisible]);

  return (
    <div
      ref={mediaRef}
      className={`p-filter-tab__column p-filter-tab__column--media ${isVisible ? "visible" : "hidden"}`}
    >
      <ImageLoader
        className="p-filter-tab__image p-filter-tab__image--desktop img-relative"
        placeholderAspectRatio={788 / 754}
        image={filterData.image.src}
        webp={filterData.image.webp}
        alt={filterData.title}
      />
    </div>
  );
}
